import { useEffect, useRef, useState, type RefObject } from 'react';

type NavMode = 'dark' | 'light';

// ── Color helpers ──

interface RGBA { r: number; g: number; b: number; a: number }

function parseRGBA(str: string | null | undefined): RGBA | null {
  if (!str || str === 'transparent') return null;
  // Modern space-separated: rgb(0 0 0 / 0.9) or rgba(0 0 0 / 0.9)
  let m = str.match(/rgba?\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*([\d.]+))?\)/);
  if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };
  // Legacy comma-separated: rgb(0, 0, 0) or rgba(0, 0, 0, 0.9)
  m = str.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/);
  if (!m) return null;
  return { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };
}

function alphaOver(base: { r: number; g: number; b: number }, fg: RGBA): { r: number; g: number; b: number } {
  return {
    r: fg.r * fg.a + base.r * (1 - fg.a),
    g: fg.g * fg.a + base.g * (1 - fg.a),
    b: fg.b * fg.a + base.b * (1 - fg.a),
  };
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

// ── Gradient parser ──

function parseGradientColor(sampleY: number, el: Element, bgImage: string): RGBA | null {
  try {
    // Only handle linear-gradient
    const match = bgImage.match(/linear-gradient\(([^)]+(?:\([^)]*\))*[^)]*)\)/);
    if (!match) return null;
    const inner = match[1];

    // Determine direction
    let flipped = false;
    let directionEnd = 0;
    if (inner.startsWith('to ')) {
      const dirMatch = inner.match(/^to\s+(top|bottom)/);
      if (dirMatch) {
        flipped = dirMatch[1] === 'top';
        directionEnd = inner.indexOf(',', dirMatch[0].length);
      }
    } else if (/^\d+deg/.test(inner)) {
      const degMatch = inner.match(/^(\d+)deg/);
      if (degMatch) {
        const deg = +degMatch[1];
        flipped = deg >= 135 && deg <= 315;
        directionEnd = inner.indexOf(',', degMatch[0].length);
      }
    }

    const colorPart = directionEnd > 0 ? inner.slice(directionEnd + 1).trim() : inner;

    // Extract color stops - handle both legacy rgba(r,g,b,a) and modern rgb(r g b / a)
    const stopRegex = /rgba?\([\d.\s,/]+\)/g;
    const stops: RGBA[] = [];
    let m2;
    while ((m2 = stopRegex.exec(colorPart)) !== null) {
      const c = parseRGBA(m2[0]);
      if (c) stops.push(c);
    }
    if (stops.length < 2) return null;

    const rect = el.getBoundingClientRect();
    if (rect.height === 0) return null;
    let ratio = (sampleY - rect.top) / rect.height;
    ratio = Math.max(0, Math.min(1, ratio));
    if (flipped) ratio = 1 - ratio;

    // Interpolate between nearest pair
    const segLen = 1 / (stops.length - 1);
    const idx = Math.min(Math.floor(ratio / segLen), stops.length - 2);
    const localT = (ratio - idx * segLen) / segLen;
    const a = stops[idx], b = stops[idx + 1];
    return {
      r: lerp(a.r, b.r, localT),
      g: lerp(a.g, b.g, localT),
      b: lerp(a.b, b.b, localT),
      a: lerp(a.a, b.a, localT),
    };
  } catch {
    return null;
  }
}

// ── Image sampler (1×1 canvas) ──

const sampleCanvas = typeof OffscreenCanvas !== 'undefined'
  ? new OffscreenCanvas(1, 1)
  : (() => { const c = document.createElement('canvas'); c.width = 1; c.height = 1; return c; })();
const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;

function sampleImage(img: HTMLImageElement, x: number, y: number): RGBA | null {
  if (!sampleCtx) return null;
  try {
    const rect = img.getBoundingClientRect();
    if (!img.naturalWidth || !img.naturalHeight) return null;
    const sx = ((x - rect.left) / rect.width) * img.naturalWidth;
    const sy = ((y - rect.top) / rect.height) * img.naturalHeight;
    sampleCtx.clearRect(0, 0, 1, 1);
    sampleCtx.drawImage(img, sx, sy, 1, 1, 0, 0, 1, 1);
    const d = sampleCtx.getImageData(0, 0, 1, 1).data;
    return { r: d[0], g: d[1], b: d[2], a: d[3] / 255 };
  } catch {
    return null; // CORS
  }
}

// ── Composite one sample point ──

function compositeAt(x: number, y: number, navWrapper: Element): { r: number; g: number; b: number } {
  const bodyBg = parseRGBA(getComputedStyle(document.body).backgroundColor);
  let base = bodyBg ? { r: bodyBg.r, g: bodyBg.g, b: bodyBg.b } : { r: 128, g: 128, b: 128 };

  let stack: Element[];
  try {
    stack = Array.from(document.elementsFromPoint(x, y));
  } catch {
    return base;
  }

  // Back-to-front (reverse the z-ordered array)
  stack.reverse();

  for (const el of stack) {
    const tag = el.tagName.toLowerCase();
    if (tag === 'html' || tag === 'body') continue;
    if (navWrapper.contains(el)) continue;

    // Try image
    if (tag === 'img') {
      const color = sampleImage(el as HTMLImageElement, x, y);
      if (color && color.a > 0.01) {
        base = alphaOver(base, color);
      }
      continue;
    }

    // Try computed styles
    let cs: CSSStyleDeclaration;
    try {
      cs = getComputedStyle(el);
    } catch {
      continue;
    }

    // Gradient
    const bgImage = cs.backgroundImage;
    if (bgImage && bgImage !== 'none' && bgImage.includes('gradient')) {
      const gc = parseGradientColor(y, el, bgImage);
      if (gc && gc.a > 0.01) {
        base = alphaOver(base, gc);
        continue;
      }
    }

    // Solid background
    const bg = parseRGBA(cs.backgroundColor);
    if (bg && bg.a > 0.01) {
      base = alphaOver(base, bg);
    }
  }

  return base;
}

// ── Hook ──

export function useNavLuminance(navRef: RefObject<HTMLElement | null>): NavMode {
  const [mode, setMode] = useState<NavMode>('dark');
  const modeRef = useRef<NavMode>('dark');
  const rafId = useRef(0);
  const contentTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const sample = () => {
      // Skip if tab is backgrounded
      if (document.visibilityState === 'hidden') return;

      const nav = navRef.current;
      if (!nav) return;
      const innerNav = nav.querySelector('nav');
      if (!innerNav) return;

      const rect = innerNav.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      // Skip if nav is off-screen (hidden by scroll)
      if (rect.top > window.innerHeight || rect.bottom < 0) return;

      const cy = rect.top + rect.height / 2;
      const points = [0.25, 0.5, 0.75];
      let totalR = 0, totalG = 0, totalB = 0;

      for (const pct of points) {
        const px = rect.left + rect.width * pct;
        const c = compositeAt(px, cy, nav);
        totalR += c.r;
        totalG += c.g;
        totalB += c.b;
      }

      const avgR = totalR / 3;
      const avgG = totalG / 3;
      const avgB = totalB / 3;
      const lum = 0.299 * avgR + 0.587 * avgG + 0.114 * avgB;

      let next: NavMode;
      if (lum < 210) next = 'dark';
      else if (lum > 235) next = 'light';
      else next = modeRef.current; // hysteresis

      if (next !== modeRef.current) {
        modeRef.current = next;
        setMode(next);
      }
    };

    const scheduleRaf = () => {
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(sample);
    };

    // Scroll/resize: single-frame debounce (responsive)
    const onScroll = () => scheduleRaf();

    // DOM mutations: settle-based debounce (150ms) to batch React render storms
    const onContentChange = () => {
      if (contentTimerId.current) clearTimeout(contentTimerId.current);
      contentTimerId.current = setTimeout(scheduleRaf, 150);
    };

    // Re-sample when tab becomes visible again
    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleRaf();
    };

    // Initial sample
    requestAnimationFrame(sample);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    const innerScrollers = document.querySelectorAll('.overflow-y-auto');
    innerScrollers.forEach(el => el.addEventListener('scroll', onScroll, { passive: true } as EventListenerOptions));

    // Theme toggle: double-rAF to let styles settle
    const themeObserver = new MutationObserver(() => {
      requestAnimationFrame(() => requestAnimationFrame(sample));
    });
    themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // DOM content changes: use settle debounce instead of rAF
    const contentObserver = new MutationObserver(onContentChange);
    const root = document.getElementById('root');
    if (root) {
      contentObserver.observe(root, { childList: true, subtree: true });
    }

    return () => {
      cancelAnimationFrame(rafId.current);
      if (contentTimerId.current) clearTimeout(contentTimerId.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      innerScrollers.forEach(el => el.removeEventListener('scroll', onScroll));
      themeObserver.disconnect();
      contentObserver.disconnect();
    };
  }, [navRef]);

  return mode;
}
